import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import List from '@mui/material/List';
import Button from '@mui/material/Button';
import { isPast, isToday } from 'date-fns';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { Box, ButtonBase, Fade, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { BillingScheduleCollectionResponse } from '@acorn-portal/shared/src/services/managedPaymentsApi';
import { PaymentScheduleDetails, usePaymentSchedule } from '@acorn-portal/shared/src/components/paymentDetails';

interface IFullPaymentScheduleList {
	collections: BillingScheduleCollectionResponse[];
}

const FullPaymentScheduleList: React.FC<IFullPaymentScheduleList> = ({ collections }: IFullPaymentScheduleList) => {
	const [rowsToShow, setRowsToShow] = useState(5);
	const [showPrevious, setShowPrevious] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [selectedCollection, setSelectedCollection] = useState<BillingScheduleCollectionResponse | undefined>(
		undefined,
	);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { sortedRows, getTextStyle, getDescription, formatDate } = usePaymentSchedule(collections);

	const firstOverduePayment = sortedRows.find((row) => row.overdue === true);

	const futureAndOverdueRows = sortedRows.filter(
		(row) => !isPast(new Date(row.scheduledDate)) || isToday(new Date(row.scheduledDate)),
	);

	if (firstOverduePayment && !futureAndOverdueRows.includes(firstOverduePayment)) {
		futureAndOverdueRows.unshift(firstOverduePayment);
	}

	const visibleRows = showPrevious ? sortedRows : futureAndOverdueRows;

	const hasPreviousPayments = sortedRows.some(
		(row) => isPast(new Date(row.scheduledDate)) && !isToday(new Date(row.scheduledDate)),
	);

	const getIcon = (rowData: BillingScheduleCollectionResponse) => {
		switch (rowData.paymentMethod?.settlementMethodCode) {
			case 'DDM':
				return <AccountBalanceIcon />;
			case 'CARD':
				return <CreditCardIcon />;
			default:
				return <CreditCardIcon />;
		}
	};

	const showDetails = (rowData: BillingScheduleCollectionResponse) => {
		setSelectedCollection(rowData);
		setOpenModal(true);
	};

	return (
		<>
			{hasPreviousPayments && (
				<Box sx={{ display: 'flex', justifyContent: 'center', m: 1 }}>
					<Button
						variant='text'
						onClick={() => setShowPrevious(!showPrevious)}
						color='primary'
						sx={{ ':hover': { backgroundColor: 'unset' } }}
					>
						{showPrevious ? 'Hide Previous Payments' : 'Show Previous Payments'}
					</Button>
				</Box>
			)}

			<List>
				{visibleRows.slice(0, rowsToShow).map((row, index) => (
					<React.Fragment key={index}>
						<Fade in timeout={500}>
							<Box
								sx={{
									borderRadius: 1,
									...(row.status === 'AUTHORISED' && {
										color: theme.palette.grey[500],
									}),
								}}
							>
								<Box
									sx={{
										backgroundColor: theme.palette.grey[200],
										color: theme.palette.common.black,
										p: 1,
										borderTop: `1px solid ${theme.palette.divider}`,
										borderBottom: `1px solid ${theme.palette.divider}`,
									}}
								>
									{formatDate(row)}
								</Box>

								<Box
									sx={{
										display: 'flex',
										alignItems: 'left',
										p: 2,
									}}
								>
									<ButtonBase sx={{ display: 'flex', width: '100%' }} centerRipple onClick={() => showDetails(row)}>
										<Box
											sx={{
												borderRadius: '50%',
												width: 40,
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												backgroundColor: theme.palette.background.paper,
												boxShadow: theme.shadows[1],
												marginRight: 2,
											}}
										>
											{getIcon(row)}
										</Box>

										<Box flexGrow={1} textAlign='left' paddingLeft={2}>
											<Typography variant='subtitle2' sx={{ ...getTextStyle(row) }}>
												{getDescription(row)}
											</Typography>
											<Typography variant='h6' color='secondary' style={getTextStyle(row)}>
												{formatCurrency(row.amount)}
											</Typography>
										</Box>
										<Box flexGrow={1} sx={{ color: theme.palette.grey[500] }} textAlign='end'>
											<ArrowForwardIosIcon />
										</Box>
									</ButtonBase>
								</Box>
							</Box>
						</Fade>
					</React.Fragment>
				))}
			</List>

			{isMobile && visibleRows.length > rowsToShow && (
				<Box sx={{ display: 'flex', justifyContent: 'center', m: 1 }}>
					<Button
						variant='text'
						onClick={() => setRowsToShow(rowsToShow + 5)}
						color='primary'
						sx={{ ':hover': { backgroundColor: 'unset' } }}
					>
						Show More
					</Button>
				</Box>
			)}
			{selectedCollection && (
				<PaymentScheduleDetails open={openModal} onClose={() => setOpenModal(false)} collection={selectedCollection} />
			)}
		</>
	);
};

export default FullPaymentScheduleList;
